<template>
  <div class="min-h-screen">
    <TContainer
      size="xl"
      class="py-10 lg:py-14"
    >
      <div class="bg-primary-50 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-x-6">
        <template v-if="unpublishedRv">
          <div class="space-y-2 p-4">
            <THeading :level="5">
              <I18nT
                keypath="headingWithName"
                tag="span"
              >
                <template #name>
                  <span class="text-gray-500">{{ formattedName }}</span>
                </template>
              </I18nT>
            </THeading>

            <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-500">
              <TBadge
                variant="default"
                is-inverted
              >
                {{ t('unpublished') }}
              </TBadge>
              <span class="ml-2">{{ formattedLocation }}</span>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            v-if="rvPhotoUrl"
            class="max-sm:hidden flex-none"
          >
            <img
              :src="rvPhotoUrl"
              :alt="rvRVName ?? undefined"
              :width="preset.width"
              :height="preset.height"
              class="object-cover h-30 rounded-l-xl sm:w-auto"
            >
          </div>

          <div class="min-w-0 space-y-2 p-4 sm:p-0 sm:pr-4">
            <THeading :level="5">
              <template v-if="formattedName">
                <I18nT
                  keypath="headingWithName"
                  tag="span"
                >
                  <template #name>
                    <NuxtLink
                      :to="localePath({ name: 'rv-rental-alias', params: { alias: rvAliasName ?? '' } })"
                      class="text-secondary hover:underline"
                    >{{ formattedName }}</NuxtLink>
                  </template>
                </I18nT>
              </template>
              <template v-else>
                {{ t('headingGeneric') }}
              </template>
            </THeading>

            <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-500">
              <span>{{ t(rvLocalizedRVType) }}</span>
              <span class="before:content-['·'] before:mr-2">{{ t('rv.sleeps') }} {{ rvGuests }}</span>
              <span class="before:content-['·'] before:mr-2">{{ rvYear }}</span>
              <span class="before:content-['·'] before:mr-2">{{ formattedLocation }}</span>
            </div>
          </div>
        </template>
      </div>
    </TContainer>

    <TContainer
      size="xl"
      class="pb-12 lg:pb-16 space-y-8"
    >
      <Grid
        :max-results="similarRvs?.ListRVs?.length"
        :sm-cols="2"
        :md-cols="3"
      >
        <CardRv
          v-for="rv in similarRvs?.ListRVs"
          :key="rv.Id"
          :rv="rv"
          cta="similar"
          discovery-source="SimilarRV"
          search-request-id=""
        />
      </Grid>

      <div class="flex flex-col items-center gap-2 text-center mt-12">
        <p class="font-medium">
          {{ t('searchFallbackText') }}
        </p>
        <TButton
          variant="highlight"
          @click="navigateToRvSearch"
        >
          {{ t('searchFallbackCta') }}
        </TButton>
      </div>
    </TContainer>
  </div>
</template>

<script setup lang="ts">
import type { RvUnpublishedError } from '~/types/errors'

definePageMeta({
  i18n: {
    paths: {
      en: '/rv-rental/[alias]/similar',
      fr: '/location-vr/[alias]/similaire',
    },
  },
})

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { $captureError } = useNuxtApp()
const { getDynamicImage, presets } = useImage()
const { formatAsDateISO } = useDates()
const { navigateToRvSearch } = useSearch()

const alias = route.params.alias as string

const { data: rvData, error: rvError } = await useRentals('/api/rvlistings/details', {
  query: {
    name: alias,
  },
})

const unpublishedRv = computed(() => rvError.value?.data as unknown as RvUnpublishedError)

if (rvError.value && !unpublishedRv.value?.RVId) {
  $captureError(rvError.value)
  throw createError({ ...rvError.value, fatal: true })
}

const start = getQueryParameter(route.query, 'StartDate')
const end = getQueryParameter(route.query, 'EndDate')

const { data: similarRvs, error: similarRvsError } = await useRentals('/api/rvlistings/similar-rvs', {
  query: {
    RVId: rvData.value?.Id ?? unpublishedRv.value?.RVId,
    StartDate: start ? formatAsDateISO(start) : null,
    EndDate: end ? formatAsDateISO(end) : null,
    RVCount: 30,
  },
})

if (similarRvsError.value) {
  $captureError(similarRvsError.value)
  throw createError({ ...similarRvsError.value, fatal: true })
}

const { rvRVName, rvAliasName, rvGuests, rvYear, rvLocalizedRVType, rvMainPhotoPath, rvLocation } = useRvDetails(rvData)

const preset = presets.LANDSCAPE_SM
const rvPhotoUrl = computed(() => {
  const path = rvMainPhotoPath.value
  if (!path) {
    return null
  }

  return getDynamicImage({ path, preset })
})

const formattedName = computed(() => {
  if (unpublishedRv.value) {
    return unpublishedRv.value?.Name
  }

  return rvRVName.value
})

const formattedLocation = computed(() => {
  if (unpublishedRv.value) {
    return [unpublishedRv.value?.City, unpublishedRv.value?.State, unpublishedRv.value?.Country].filter(Boolean).join(', ')
  }

  return [rvLocation.value?.City, rvLocation.value?.State, rvLocation.value?.Country].filter(Boolean).join(', ')
})

useSeoMeta({
  title: () => formattedName.value ? t('metaTitleWithName', { name: formattedName.value }) : t('title'),
  robots: {
    noindex: true,
    follow: true,
  },
})
</script>

<i18n lang="json">
{
  "en": {
    "title": "Similar RVs",
    "metaTitleWithName": "RVs similar to {name}",
    "headingWithName": "Looking for an RV similar to {name}?",
    "headingGeneric": "Looking for a similar RV?",
    "searchFallbackText": "Didn't find what you're looking for?",
    "searchFallbackCta": "Search all RVs",
    "unpublished": "Unpublished"
  },
  "fr": {
    "title": "VRs similaires",
    "metaTitleWithName": "VRs similaires à {name}",
    "headingWithName": "Vous cherchez un VR similaire à {name}?",
    "headingGeneric": "Vous cherchez un VR similaire?",
    "searchFallbackText": "Vous n'avez pas trouvé ce que vous cherchiez?",
    "searchFallbackCta": "Rechercher tous les VRs",
    "unpublished": "Non publié"
  }
}
</i18n>
